import React, { useState, useEffect } from "react";
import { Mail, Lock, User, Loader2, ArrowRight, Check, Sparkles, FileText } from "lucide-react";
import basiqLogo from "../assets/basiq.png";
import basiqIcon from "../assets/basiq-icon.png";
import { registerUser, loginUser } from "../lib/api";
import { startGoogleLogin, clearAuthTab } from "../lib/authFlow";

function formatErr(detail) {
  if (detail == null) return "Algo deu errado. Tente novamente.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && e.msg) || "").filter(Boolean).join(" ") || "Verifique os dados informados.";
  if (detail && detail.msg) return detail.msg;
  return "Algo deu errado. Tente novamente.";
}

export default function Login({ onAuthed, pendingPrompt, initialTab = "login" }) {
  const [tab, setTab] = useState(initialTab);
  useEffect(() => { clearAuthTab(); }, []);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const isRegister = tab === "register";

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true); setError("");
    try {
      const user = isRegister
        ? await registerUser({ name, email, password })
        : await loginUser({ email, password });
      onAuthed(user);
    } catch (err) {
      setError(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#fafafa] relative overflow-hidden flex flex-col items-center justify-center px-5 py-10" data-testid="login-page">
      <div className="pointer-events-none absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full opacity-60 blur-3xl" style={{ background: "radial-gradient(circle at center, #E5E1FA, transparent 70%)" }} />
      <div className="pointer-events-none absolute -bottom-52 -right-40 w-[560px] h-[560px] rounded-full opacity-40 blur-3xl" style={{ background: "radial-gradient(circle at center, rgba(74,59,250,0.16), transparent 70%)" }} />

      <div className="relative w-full max-w-[400px] float-up text-center">
        <img src={basiqIcon} alt="" className="w-14 h-14 mx-auto rounded-2xl shadow-[0_14px_34px_-12px_rgba(74,59,250,0.55)]" />
        <img src={basiqLogo} alt="Basiq" className="h-7 w-auto mx-auto object-contain mt-4" data-testid="login-logo" />
        <p className="mt-3 text-[15px] text-slate-500">Orçamentos. Sem complicação.</p>

        <div className="mt-8 bg-white ring-1 ring-slate-200 rounded-3xl p-7 shadow-[0_24px_60px_-30px_rgba(15,23,42,0.22)] text-left">
          <div className="grid grid-cols-2 p-1 rounded-xl bg-slate-100 text-[13px] font-medium" data-testid="login-tabs">
            {[["login", "Entrar"], ["register", "Criar conta"]].map(([k, l]) => (
              <button key={k} type="button" onClick={() => { setTab(k); setError(""); }} data-testid={`login-tab-${k}`}
                className={`h-9 rounded-lg transition-all ${tab === k ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
                {l}
              </button>
            ))}
          </div>

          <h1 className="mt-6 text-xl font-semibold tracking-tight text-slate-900">
            {isRegister ? "Crie sua conta grátis" : "Bem-vindo(a) de volta"}
          </h1>
          <p className="mt-1 text-[13px] text-slate-500">
            {isRegister ? "Comece a gerar orçamentos em segundos." : "Entre para continuar no Basiq."}
          </p>

          <form onSubmit={submit} className="mt-5 space-y-3">
            {isRegister && <Field icon={User} type="text" placeholder="Seu nome" value={name} onChange={setName} testid="auth-name" />}
            <Field icon={Mail} type="email" placeholder="E-mail" value={email} onChange={setEmail} testid="auth-email" />
            <Field icon={Lock} type="password" placeholder={isRegister ? "Senha (mín. 6 caracteres)" : "Senha"} value={password} onChange={setPassword} testid="auth-password" />

            {error && <div className="text-[12.5px] text-rose-600 bg-rose-50 rounded-lg px-3 py-2" data-testid="auth-error">{error}</div>}

            <button type="submit" disabled={busy} data-testid="auth-submit"
              className="w-full h-11 rounded-xl text-white text-[14px] font-semibold inline-flex items-center justify-center gap-2 transition-all hover:brightness-110 active:scale-[0.99] disabled:opacity-60"
              style={{ background: "#4A3BFA", boxShadow: "0 12px 26px -12px rgba(74,59,250,0.6)" }}>
              {busy ? <Loader2 size={17} className="animate-spin" /> : <>{isRegister ? "Criar conta" : "Entrar"} <ArrowRight size={16} /></>}
            </button>
          </form>

          <div className="flex items-center gap-3 my-5">
            <div className="h-px flex-1 bg-slate-100" />
            <span className="text-[11px] text-slate-400 uppercase tracking-wide">ou</span>
            <div className="h-px flex-1 bg-slate-100" />
          </div>

          <button type="button" onClick={() => startGoogleLogin(pendingPrompt)} data-testid="google-login-btn"
            className="w-full h-11 rounded-xl ring-1 ring-slate-200 hover:ring-slate-300 hover:bg-slate-50 text-[14px] font-medium text-slate-700 inline-flex items-center justify-center gap-2.5 transition-all">
            <GoogleG /> Continuar com Google
          </button>

          <div className="mt-6 space-y-2.5">
            {[
              { icon: Sparkles, t: "IA monta o orçamento a partir de um texto" },
              { icon: FileText, t: "10 modelos A4 premium e PDF em alta" },
              { icon: Check, t: "Várias empresas, cada uma com seu estilo" },
            ].map((f, i) => (
              <div key={i} className="flex items-center gap-2.5 text-[12.5px] text-slate-500">
                <span className="grid place-items-center w-6 h-6 rounded-lg bg-slate-100 text-slate-600 shrink-0"><f.icon size={13} /></span>
                {f.t}
              </div>
            ))}
          </div>
        </div>

        <p className="mt-6 text-[11px] text-slate-400">Ao entrar você concorda com nossos termos de uso.</p>
      </div>
    </div>
  );
}

const Field = ({ icon: Icon, type, placeholder, value, onChange, testid }) => (
  <div className="relative">
    <Icon size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid} required
      className="w-full h-11 rounded-xl bg-white ring-1 ring-slate-200 focus:ring-2 focus:ring-[#4A3BFA] outline-none pl-10 pr-3.5 text-[14px] text-slate-800 placeholder:text-slate-400 transition-all" />
  </div>
);

const GoogleG = () => (
  <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden>
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);
